import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchLocations as fetchLocationsFromApi } from '../lib/api/locations';
import { useAuth } from './AuthContext';

const LocationsContext = createContext({
  locations: [],
  loading: true,
  error: null,
  refetch: () => Promise.resolve([]),
});

export const LocationsProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchLocationsFromApi();
      if (isMountedRef.current) {
        setLocations(result);
        hasFetchedRef.current = true;
      }
      return result;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        setLocations([]);
        hasFetchedRef.current = false;
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      setLocations([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }

    loadLocations().catch(() => {
      // Error state handled separately; suppress unhandled rejection warning
    });
  }, [authLoading, isAuthenticated, loadLocations]);

  const value = useMemo(
    () => ({
      locations,
      loading,
      error,
      refetch: loadLocations,
    }),
    [locations, loading, error, loadLocations],
  );

  return (
    <LocationsContext.Provider value={value}>
      {children}
    </LocationsContext.Provider>
  );
};

export const useLocations = () => useContext(LocationsContext);
